/**
 * Competitions section — typed API helpers + TanStack Query hooks.
 *
 * Backend routes live in `backend/src/routes/admin-competitions.ts`. The
 * Create-comp form posts to the existing `/v1/admin/competitions` handler in
 * `routes/admin.ts` (per brief Q11 — preserved edit at v1).
 */

import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import { api } from '@/api/client';

// ---------------- Shared list-response shape -----------------

export interface ListMeta {
  count: number;
  total: number;
  limit: number;
  offset: number;
}

export interface ListResponse<T> {
  data: T[];
  meta: ListMeta;
}

// ---------------- Competitions list -----------------

export type CompetitionType = 'official' | 'public' | 'private';
export type CompetitionProductType = 'fantasy' | 'predictions';
export type CompetitionStatus = 'OPEN' | 'LOCKED' | 'COMPLETED' | 'SCORED' | 'CANCELLED';
export type CompetitionFormat = 'ONE_OFF' | 'SERIES';
/**
 * Game-mode discriminator for fresh preview administration. EPL 26/27 is the
 * active fantasy game; WC remains selectable only for compatible archived
 * screens while prediction creation is retired in fresh-start.
 */
export type AdminGameKey = 'football_epl2627' | 'football_wc26';

export interface AdminCompetitionRow {
  id: string;
  slug: string | null;
  title: string;
  status: string;
  format: string;
  visibility: string;
  official: boolean;
  type: CompetitionType;
  invite_token: string | null;
  budget_cap: number;
  squad_size: number;
  start_round: number;
  end_round: number;
  entry_deadline: string | null;
  created_at: string;
  updated_at: string;
  scored_at: string | null;
  league_name: string;
  sport: string;
  game_key: string;
  product_type: CompetitionProductType;
  created_by: string | null;
  created_by_kind: 'user' | 'agent' | 'admin' | null;
  entrant_count: number;
  result_count: number;
  round_window: string;
}

export type CompetitionsSortKey = 'created_at' | 'entrants';

export interface CompetitionsFilters {
  q?: string;
  type?: CompetitionType | 'any';
  status?: CompetitionStatus | 'any';
  format?: CompetitionFormat | 'any';
  product_type?: CompetitionProductType | 'any';
  game_key?: AdminGameKey | 'any';
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
  /** Server-side column sort (the list is paginated). */
  sort?: CompetitionsSortKey;
  dir?: 'asc' | 'desc';
}

export function useAdminCompetitions(filters: CompetitionsFilters) {
  return useQuery({
    queryKey: ['admin', 'competitions', 'list', filters],
    queryFn: () =>
      api.get<ListResponse<AdminCompetitionRow>>(buildPath('/v1/admin/competitions/list', filters)),
    placeholderData: keepPreviousData,
  });
}

// ---------------- Competition detail -----------------

export interface AdminCompetitionRound {
  id: string;
  sequence: number;
  round: number | null;
  fixture_scope: string | null;
  status: string;
  lock_at: string;
  scored_at: string | null;
}

export interface AdminCompetitionEntrantPreview {
  id: string;
  agent_id: string;
  agent_name: string;
  agent_slug: string | null;
  owner_user_id: string | null;
  created_at: string;
}

export interface AdminCompetitionDetail {
  id: string;
  slug: string | null;
  title: string;
  status: string;
  format: string;
  visibility: string;
  official: boolean;
  type: CompetitionType;
  invite_token: string | null;
  budget_cap: number;
  squad_size: number;
  start_round: number;
  end_round: number;
  round_window: string;
  entry_deadline: string | null;
  created_at: string;
  updated_at: string;
  scored_at: string | null;
  league_name: string;
  sport: string;
  game_key: string;
  product_type: CompetitionProductType;
  scoring_version: string;
  rule_set: unknown;
  max_entrants: number | null;
  creator_share_headline: string | null;
  creator_share_body: string | null;
  creator_cover_url: string | null;
  creator_id: string | null;
  created_by: string | null;
  created_by_kind: 'user' | 'agent' | 'admin' | null;
  entrant_count: number;
  result_count: number;
  rounds: AdminCompetitionRound[];
  first_entrants: AdminCompetitionEntrantPreview[];
}

export function useAdminCompetition(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'competition', id],
    queryFn: () => api.get<AdminCompetitionDetail>(`/v1/admin/competitions/${id}/detail`),
    enabled: !!id,
  });
}

export interface UpdateCompetitionPrizeInput {
  competitionId: string;
  prize_description: string | null;
  prize_image_url: string | null;
}

export function useUpdateCompetitionPrize() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      competitionId,
      prize_description,
      prize_image_url,
    }: UpdateCompetitionPrizeInput) =>
      api.patch<{
        competition: Pick<
          AdminCompetitionDetail,
          | 'id'
          | 'slug'
          | 'title'
          | 'creator_share_headline'
          | 'creator_share_body'
          | 'creator_cover_url'
        >;
      }>(`/v1/admin/competitions/${competitionId}/prize`, { prize_description, prize_image_url }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin', 'competition', variables.competitionId] });
      qc.invalidateQueries({ queryKey: ['admin', 'competitions', 'list'] });
    },
  });
}

// ---------------- Entries -----------------

export interface AdminEntryRow {
  id: string;
  status: string;
  created_at: string;
  llm_model: string | null;
  agent_id: string;
  agent_name: string;
  agent_slug: string | null;
  owner_user_id: string | null;
  owner_email: string | null;
  competition_id: string;
  competition_title: string;
  competition_slug: string | null;
  competition_status: string;
  competition_format: string;
  competition_official: boolean;
  competition_visibility: string;
  competition_round_window: string;
  squad_cost: number | null;
  total_points: number;
  best_rank: number | null;
}

export interface EntriesFilters {
  competition_id?: string;
  agent_id?: string;
  owner_id?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export function useAdminEntries(filters: EntriesFilters) {
  return useQuery({
    queryKey: ['admin', 'entries', filters],
    queryFn: () => api.get<ListResponse<AdminEntryRow>>(buildPath('/v1/admin/entries', filters)),
    placeholderData: keepPreviousData,
  });
}

export interface AdminEntrySquadPlayer {
  player_id: string;
  player_name: string;
  team_name: string;
  position: string;
  selected_position: string;
  cost: number;
  is_active: boolean;
  is_captain: boolean;
  is_starter: boolean;
  bench_order: number | null;
}

export interface AdminEntryDetail {
  id: string;
  status: string;
  created_at: string;
  llm_model: string | null;
  agent: {
    id: string;
    name: string;
    slug: string | null;
    owner_user_id: string | null;
    owner_email: string | null;
    owner_verified_human: boolean;
  };
  competition: {
    id: string;
    title: string;
    slug: string | null;
    status: string;
    format: string;
    official: boolean;
    visibility: string;
    start_round: number;
    end_round: number;
    round_window: string;
    budget_cap: number;
  };
  squad: {
    id: string;
    name: string | null;
    banter: string | null;
    total_cost: number;
    valid: boolean;
    coach_id: string | null;
    coach_country: string | null;
    coach_name: string | null;
    active_superpower: string | null;
    superpower_target_id: string | null;
    transfers_used: number;
    players: AdminEntrySquadPlayer[];
  } | null;
  performance: {
    total_points: number;
    best_rank: number | null;
    result_count: number;
  };
}

export function useAdminEntryDetail(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'entries', 'detail', id],
    queryFn: () => api.get<AdminEntryDetail>(`/v1/admin/entries/${id}/detail`),
    enabled: !!id,
  });
}

// ---------------- Leaderboard -----------------

export interface AdminLeaderboardRow {
  rank: number;
  agent_id: string;
  agent_name: string;
  agent_slug: string | null;
  owner_user_id: string | null;
  llm_model: string | null;
  total_points: number;
  scoring_breakdown?: unknown;
  round_sequence?: number;
  rounds_played?: number;
  predictions_count?: number;
  exact_count?: number;
  outcome_count?: number;
}

export interface AdminLeaderboardResponse {
  competition: {
    id: string;
    title: string;
    format: string;
    status: string;
    scope: 'cumulative' | 'round';
    round_sequence: number | null;
    product_type?: CompetitionProductType;
  };
  rounds: Array<{
    id: string;
    sequence: number;
    round: number | null;
    status: string;
  }>;
  data: AdminLeaderboardRow[];
  meta: { count: number };
}

export function useAdminLeaderboard(competitionId: string | null, round?: number) {
  return useQuery({
    queryKey: ['admin', 'leaderboard', competitionId, round ?? null],
    queryFn: () =>
      api.get<AdminLeaderboardResponse>(
        round
          ? `/v1/admin/competitions/${competitionId}/leaderboard?round=${round}`
          : `/v1/admin/competitions/${competitionId}/leaderboard`,
      ),
    enabled: !!competitionId,
  });
}

// ---------------- Per-round detail (rebuilt admin Rounds tab) -----------------

export interface AdminRoundFixture {
  id: string;
  home_team: string;
  away_team: string;
  home_score: number | null;
  away_score: number | null;
  kickoff: string;
  status: string;
}

export interface AdminRoundDetail {
  id: string;
  sequence: number;
  round: number | null;
  fixture_scope: string | null;
  scope_code: string | null;
  status: string;
  lock_at: string;
  scored_at: string | null;
  fixture_count: number;
  fixtures_completed: number;
  fixtures_live: number;
  fixtures_scheduled: number;
  entries_scored: number;
  top_scorer: { agent_id: string; agent_name: string; score: number } | null;
  fixtures: AdminRoundFixture[];
}

export interface AdminRoundsDetailResponse {
  data: AdminRoundDetail[];
  meta: { count: number };
}

export function useAdminCompetitionRoundsDetail(competitionId: string | null) {
  return useQuery({
    queryKey: ['admin', 'competition', competitionId, 'rounds-detail'],
    queryFn: () =>
      api.get<AdminRoundsDetailResponse>(`/v1/admin/competitions/${competitionId}/rounds-detail`),
    enabled: !!competitionId,
  });
}

// ---------------- Per-agent player breakdown -----------------

export interface AdminAgentBreakdownRoundCell {
  round_id: string;
  sequence: number;
  scope_code: string | null;
  fixture_scope: string | null;
  round_status: string;
  points: number | null;
  is_captain: boolean;
}

export interface AdminAgentBreakdownPlayerRow {
  player_id: string;
  player_name: string;
  team_name: string;
  position: string;
  country_code: string | null;
  cost: number;
  is_captain: boolean;
  is_starter: boolean;
  bench_order: number | null;
  rounds: AdminAgentBreakdownRoundCell[];
  total: number;
}

export interface AdminAgentBreakdownResponse {
  competition: {
    id: string;
    title: string;
    slug: string | null;
    format: string;
    status: string;
    league_name: string;
    budget_cap: number;
  };
  agent: {
    id: string;
    name: string;
    slug: string | null;
    owner_user_id: string | null;
    owner_email: string | null;
  };
  entry: {
    id: string;
    status: string;
    llm_model: string | null;
    created_at: string;
  } | null;
  squad: {
    id: string;
    name: string | null;
    total_cost: number;
    valid: boolean;
    coach_id: string | null;
    coach_country: string | null;
    coach_name: string | null;
  } | null;
  rounds: Array<{
    id: string;
    sequence: number;
    round: number | null;
    scope_code: string | null;
    fixture_scope: string | null;
    status: string;
    score: number | null;
    rank: number | null;
    captain_bonus: number | null;
    coach_bonus: number | null;
    superpower_delta: number | null;
    transfer_penalty: number | null;
  }>;
  players: AdminAgentBreakdownPlayerRow[];
  total_points: number;
  meta: { count: number };
}

export function useAdminAgentBreakdown(competitionId: string | null, agentId: string | null) {
  return useQuery({
    queryKey: ['admin', 'competition', competitionId, 'agent-breakdown', agentId],
    queryFn: () =>
      api.get<AdminAgentBreakdownResponse>(
        `/v1/admin/competitions/${competitionId}/agents/${agentId}/breakdown`,
      ),
    enabled: !!competitionId && !!agentId,
  });
}

// ---------------- Invite inspector -----------------

export interface AdminInviteResponse {
  token: string;
  competition: {
    id: string;
    slug: string | null;
    title: string;
    status: string;
    format: string;
    official: boolean;
    visibility: string;
    start_round: number;
    end_round: number;
    round_window: string;
    created_at: string;
    entry_deadline: string | null;
    entrant_count: number;
  };
  creator: {
    id: string;
    kind: 'user' | 'agent';
    email: string | null;
    name: string | null;
  } | null;
  shares_count: number | null;
  signups_attributed: number | null;
  entries_attributed: number | null;
}

export function useAdminInvite(token: string | null) {
  return useQuery({
    queryKey: ['admin', 'invite', token],
    queryFn: () => api.get<AdminInviteResponse>(`/v1/admin/invites/${token}`),
    enabled: !!token,
    retry: false,
  });
}

// ---------------- Create competition (preserved edit) -----------------

export interface CreateCompetitionInput {
  title: string;
  competition_type: CompetitionProductType;
  game_key: AdminGameKey;
  /** EPL: gameweek numbers. Required for `football_epl2526`, ignored for WC. */
  rounds?: number[];
  /** WC: scope codes (e.g. `MD1`, `R32`). Required for `football_wc26`, ignored for EPL. */
  scope_codes?: string[];
  format: CompetitionFormat;
  official: boolean;
  visibility?: 'public' | 'private';
  max_entrants?: number;
  entry_deadline?: string;
}

export interface CreatedCompetition {
  id: string;
  slug: string | null;
  title: string;
}

export function useCreateCompetition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCompetitionInput) => {
      const payload: Record<string, unknown> = {
        title: input.title,
        competition_type: input.competition_type,
        game_key: input.game_key,
        format: input.format,
        official: input.official,
        visibility: input.visibility ?? 'public',
        max_entrants: input.max_entrants,
        entry_deadline: input.entry_deadline,
      };
      if (input.game_key === 'football_wc26') {
        payload.scope_codes = input.scope_codes ?? [];
      } else {
        payload.rounds = input.rounds ?? [];
      }
      return api.post<CreatedCompetition>('/v1/admin/competitions', payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'competitions', 'list'] });
    },
  });
}

// ---------------- helpers -----------------

function buildPath(base: string, filters: object): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters as Record<string, unknown>)) {
    if (v === undefined || v === null || v === '' || v === 'any') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}
